import { useContext, useEffect, useState } from 'react';
import { Box, LinearProgress, Pagination, Typography } from '@mui/material';
import {
  DataGrid,
  gridPageCountSelector,
  useGridApiContext,
  useGridSelector,
} from '@mui/x-data-grid';
import { generateUUID } from 'utils/functions';
import { DropsContext } from 'contexts/DropsContext';
import { ArmazenagemContext } from 'contexts/ArmazenagemContext';
import { findOnArray } from 'utils/functions';
import { toastWarning } from 'utils/toast';
import localeText from 'utils/localeText';
import Button from 'components/Button';
import moment from 'moment';
import styles from './styles';

const ConferenciaModal = ({ item, handleSubmit }) => {
  const [itens, setItens] = useState([]);
  const { drops } = useContext(DropsContext);
  const { getLoading, postLoading, deleteLoading } =
    useContext(ArmazenagemContext);
  const loading = getLoading || postLoading || deleteLoading;

  const CustomPagination = () => {
    const gridRef = useGridApiContext();
    const count = useGridSelector(gridRef, gridPageCountSelector);
    return (
      <Pagination
        color="primary"
        count={count}
        onChange={(_, value) => gridRef?.current?.setPage(value - 1)}
      />
    );
  };

  const renderItens = () => {
    const qtConferir = item?.quantidade - item?.quantidade_conferida;
    if (qtConferir > 0) {
      setItens([
        {
          uuid: generateUUID(itens),
          documento_item_id: item?.id,
          quantidade: qtConferir,
          validade: null,
          variacao_id: null,
          rastreabilidade: null,
        },
      ]);
    }
  };

  useEffect(renderItens, []);

  const onCellEditCommit = (e) => {
    const editedRows = itens.map((c) => {
      if (c?.uuid === e?.id) {
        if (e?.field === 'validade' && e?.value) {
          return { ...c, [e?.field]: moment(e?.value).format('YYYY-MM-DD') };
        } else if (e?.field === 'quantidade') {
          const sumQt = itens
            ?.filter((f) => f?.uuid !== e?.id)
            ?.reduce((acc, curr) => acc + (curr?.quantidade || 0), 0);

          const faltaQt = Math?.max(0, (item?.quantidade || 0) - sumQt);

          const newQt = Math.min(e?.value, faltaQt);

          return { ...c, [e?.field]: newQt };
        } else {
          return { ...c, [e?.field]: e?.value };
        }
      }
      return c;
    });

    const totalQt = editedRows?.reduce(
      (acc, curr) => acc + (curr?.quantidade || 0),
      0
    );
    const restoQt = Math?.max(0, (item?.quantidade || 0) - totalQt);

    if (restoQt > 0) {
      editedRows?.push({
        uuid: generateUUID(editedRows),
        documento_item_id: item?.id,
        quantidade: restoQt,
        validade: null,
        variacao_id: null,
        rastreabilidade: null,
      });
    }

    setItens(editedRows);
  };

  const onSubmit = () => {
    if (
      item?.variavel === 'SIM' &&
      itens?.some((s) => !Boolean(s?.variacao_id))
    ) {
      return toastWarning('Preencha a variação de todos os itens');
    }
    if (
      item?.rastreavel === 'SIM' &&
      itens?.some((s) => !Boolean(s?.rastreabilidade))
    ) {
      return toastWarning('Preencha a rastreabilidade de todos os itens');
    }

    handleSubmit(itens);
  };

  return (
    <Box sx={styles?.container}>
      <DataGrid
        rows={itens}
        columns={[
          {
            field: 'referencia',
            headerName: 'Referência',
            width: 150,
            sortable: false,
            renderCell: () => (
              <Typography variant="body2">{item?.referencia}</Typography>
            ),
          },
          {
            field: 'descricao',
            headerName: 'Produto',
            minWidth: 400,
            flex: 1,
            sortable: false,
            renderCell: () => (
              <Typography variant="body2">{item?.descricao}</Typography>
            ),
          },
          ...(item?.variavel === 'SIM'
            ? [
                {
                  field: 'variacao_id',
                  headerName: 'Variação',
                  type: 'singleSelect',
                  width: 200,
                  sortable: false,
                  editable: true,
                  renderCell: ({ value }) => (
                    <Typography variant="body2">
                      {findOnArray(value, drops?.ProdutoVariacao, 'label')}
                    </Typography>
                  ),
                  valueOptions: drops?.ProdutoVariacao?.filter(
                    (f) => f?.produto_id === item?.produto_id
                  ),
                },
              ]
            : []),
          ...(item?.rastreavel === 'SIM'
            ? [
                {
                  field: 'rastreabilidade',
                  headerName: 'Rastreabilidade',
                  width: 200,
                  sortable: false,
                  editable: true,
                },
                {
                  field: 'validade',
                  headerName: 'Validade',
                  type: 'date',
                  width: 200,
                  sortable: false,
                  editable: true,
                  valueGetter: ({ value }) =>
                    value ? moment(value, 'YYYY-MM-DD').toDate() : null,
                  renderCell: ({ value }) => (
                    <Typography variant="body2">
                      {value
                        ? moment(value, 'YYYY-MM-DD')?.format('DD/MM/YYYY')
                        : ''}
                    </Typography>
                  ),
                },
              ]
            : []),
          {
            field: 'quantidade',
            headerName: 'Quantidade',
            type: 'number',
            width: 150,
            sortable: false,
            editable: true,
            renderCell: ({ row }) => (
              <Typography variant="body2">
                {row?.quantidade || 0} {item?.unidade_medida || ''}
              </Typography>
            ),
          },
        ]}
        density="compact"
        disableColumnMenu
        showCellRightBorder
        showColumnRightBorder
        disableSelectionOnClick
        localeText={localeText}
        getRowId={(row) => row?.uuid}
        onCellEditCommit={onCellEditCommit}
        components={{
          LoadingOverlay: LinearProgress,
          Pagination: CustomPagination,
        }}
      />
      <Button
        variant="contained"
        color="primary"
        sx={styles?.btn}
        loading={loading}
        onClick={onSubmit}
      >
        Conferir
      </Button>
    </Box>
  );
};

export default ConferenciaModal;
